import { isRulebookEnabled } from '../utils/rulebookUtils';
/**
 * Service for loading and managing static game data
 * Handles careers, homeworlds, birthrights, and other character creation options
 * Includes caching, validation, and version management
 */

import {
  Career,
  Homeworld,
  Birthright,
  LureOfTheVoid,
  Trials,
  Motivation,
  Skill,
  Talent,
  Trait,
  Equipment,
  Weapon,
  Armor
} from '../types/character';
import {
  dataValidationService,
  ValidationResult,
  DataIntegrityResult
} from './DataValidationService';

// Import static data
import careersData from '../data/careers.json';
import homeworldsData from '../data/homeworlds.json';
import birthrightsData from '../data/birthrights.json';
import luresData from '../data/lures.json';
import trialsData from '../data/trials.json';
import motivationsData from '../data/motivations.json';
import skillsData from '../data/skills.json';
import talentsData from '../data/talents.json';
import traitsData from '../data/traits.json';
import shopItemsData from '../data/items.json';
import psychicPowersData from '../data/psychic-powers.json';
import type { PsychicPowerEntry } from '../types/psychic';
import { parseCommaSeparated } from '../utils/parseCommaSeparated';

/**
 * Shop item interface for items from items.json
 * Uses string `id` as the primary identifier (e.g., "laspistol", "flak-armor")
 * No numeric itemId field - all item references use string IDs
 */
interface ShopItem {
  id: string; // String ID from items.json (e.g., "laspistol", "hand-cannon")
  name: string;
  type: string;
  subtype?: string;
  weight: number;
  availability: string;
  cost: number | null;
  description: string;
  rulebook: string;
  section?: string;
  proficient?: boolean;
  weaponClass?: string;
  proficiencyRequired?: string;
  armorData?: {
    locations?: string[];
    armorPoints?: number;
    special?: string[];
  };
}

// Helper type for JSON data that might be arrays or objects with array properties and metadata
type JsonDataStructure<T> = T[] | { [key: string]: T[] | string | number };

interface GameDataCache {
  careers: Career[];
  homeworlds: Homeworld[];
  birthrights: Birthright[];
  lures: LureOfTheVoid[];
  trials: Trials[];
  motivations: Motivation[];
  skills: Skill[];
  talents: Talent[];
  traits: Trait[];
  weapons: Weapon[];
  armor: Armor[];
  equipment: Equipment[];
  shopItems: ShopItem[];
  psychicPowers: PsychicPowerEntry[];
  lastValidated: Date;
  validationResults: Record<string, ValidationResult>;
  integrityResult?: DataIntegrityResult;
}

export class GameDataService {
  private static instance: GameDataService;
  private cache: GameDataCache | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {
    this.initializeData();
  }

  /**
   * Safely extract array from JSON data structure
   */
  private extractArray<T>(data: JsonDataStructure<T>, fallbackKey?: string): T[] {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === 'object') {
      // Try the fallback key first, then try common keys
      const keys = fallbackKey ? [fallbackKey] : [];
      keys.push(...Object.keys(data));

      for (const key of keys) {
        const value = data[key];
        if (Array.isArray(value)) {
          return value as T[];
        }
      }
    }
    return [];
  }

  public static getInstance(): GameDataService {
    if (!GameDataService.instance) {
      GameDataService.instance = new GameDataService();
    }
    return GameDataService.instance;
  }

  /**
   * Initialize and validate all game data
   */
  private async initializeData(): Promise<void> {
    try {
      // Load and validate all data files
      const validationResults: Record<string, ValidationResult> = {};

      // Validate each data file
      validationResults.careers = dataValidationService.validateCareersData(careersData);
      validationResults.homeworlds = dataValidationService.validateHomeworldsData(homeworldsData);
      validationResults.birthrights =
        dataValidationService.validateBirthrightsData(birthrightsData);
      validationResults.lures = dataValidationService.validateLuresData(luresData);
      validationResults.trials = dataValidationService.validateTrialsData(trialsData);
      validationResults.motivations =
        dataValidationService.validateMotivationsData(motivationsData);
      validationResults.skills = dataValidationService.validateSkillsData(skillsData);
      validationResults.talents = dataValidationService.validateTalentsData(talentsData);
      // Equipment is now derived from items.json (shopItemsData)
      validationResults.items = dataValidationService.validateItemsData(shopItemsData);
      validationResults.traits = dataValidationService.validateTraitsData(traitsData);

      // Log validation warnings
      Object.entries(validationResults).forEach(([dataType, result]) => {
        if (!result.isValid) {
          console.error(`${dataType} data validation failed:`, result.errors);
        }
        if (result.warnings.length > 0) {
          console.warn(`${dataType} data warnings:`, result.warnings);
        }
      });

      // Extract data arrays from the structured files
      const shopItems = Array.isArray(shopItemsData) ? shopItemsData : shopItemsData.items || [];
      const gameData = {
        careers: Array.isArray(careersData) ? careersData : careersData.careers || [],
        homeworlds: Array.isArray(homeworldsData)
          ? homeworldsData
          : (homeworldsData as { homeworlds?: Homeworld[] }).homeworlds || [],
        birthrights: Array.isArray(birthrightsData)
          ? birthrightsData
          : birthrightsData.birthrights || [],
        lures: Array.isArray(luresData) ? luresData : luresData.lures || [],
        trials: Array.isArray(trialsData) ? trialsData : trialsData.trials || [],
        motivations: Array.isArray(motivationsData)
          ? motivationsData
          : motivationsData.motivations || [],
        skills: Array.isArray(skillsData) ? skillsData : skillsData.skills || [],
        talents: Array.isArray(talentsData) ? talentsData : talentsData.talents || [],
        weapons: [], // Weapons are derived from items.json
        armor: [], // Armor is derived from items.json
        equipment: [], // Equipment is derived from items.json
        traits: Array.isArray(traitsData) ? traitsData : traitsData.traits || [],
        shopItems // Include shopItems for equipment validation
      };

      // Validate data integrity
      const integrityResult = dataValidationService.validateDataIntegrity(gameData);

      if (!integrityResult.isValid) {
        console.error('Data integrity validation failed:', integrityResult.errors);
      }
      if (integrityResult.warnings.length > 0) {
        console.warn('Data integrity warnings:', integrityResult.warnings);
      }

      // Extract psychic powers
      const psychicPowers: PsychicPowerEntry[] = Array.isArray(psychicPowersData)
        ? psychicPowersData
        : (psychicPowersData as { powers?: PsychicPowerEntry[] }).powers || [];

      // Cache the validated data
      this.cache = {
        careers: gameData.careers as Career[],
        homeworlds: gameData.homeworlds as Homeworld[],
        birthrights: gameData.birthrights as Birthright[],
        lures: gameData.lures as LureOfTheVoid[],
        trials: gameData.trials as Trials[],
        motivations: gameData.motivations as Motivation[],
        skills: gameData.skills as Skill[],
        talents: gameData.talents as Talent[],
        traits: gameData.traits as Trait[],
        weapons: gameData.weapons as Weapon[],
        armor: gameData.armor as Armor[],
        equipment: gameData.equipment as Equipment[],
        shopItems: this.extractArray(
          shopItemsData as unknown as JsonDataStructure<ShopItem>,
          'items'
        ),
        psychicPowers,
        lastValidated: new Date(),
        validationResults,
        integrityResult
      };
    } catch (error) {
      console.error('Failed to initialize game data:', error);
      // Fallback to basic data without validation
      this.cache = {
        careers: this.extractArray(careersData as unknown as JsonDataStructure<Career>, 'careers'),
        homeworlds: this.extractArray(
          homeworldsData as unknown as JsonDataStructure<Homeworld>,
          'homeworlds'
        ),
        birthrights: this.extractArray(
          birthrightsData as unknown as JsonDataStructure<Birthright>,
          'birthrights'
        ),
        lures: this.extractArray(luresData as unknown as JsonDataStructure<LureOfTheVoid>, 'lures'),
        trials: this.extractArray(trialsData as unknown as JsonDataStructure<Trials>, 'trials'),
        motivations: this.extractArray(
          motivationsData as unknown as JsonDataStructure<Motivation>,
          'motivations'
        ),
        skills: this.extractArray(skillsData as unknown as JsonDataStructure<Skill>, 'skills'),
        talents: this.extractArray(talentsData as unknown as JsonDataStructure<Talent>, 'talents'),
        traits: this.extractArray(traitsData as unknown as JsonDataStructure<Trait>, 'traits'),
        weapons: [], // Weapons are derived from items.json
        armor: [], // Armor is derived from items.json
        equipment: [], // Equipment is derived from items.json
        shopItems: this.extractArray(
          shopItemsData as unknown as JsonDataStructure<ShopItem>,
          'items'
        ),
        psychicPowers: this.extractArray(
          psychicPowersData as unknown as JsonDataStructure<PsychicPowerEntry>,
          'powers'
        ),
        lastValidated: new Date(),
        validationResults: {}
      };
    }
  }

  /**
   * Ensure data is loaded and cache is valid
   */
  private ensureDataLoaded(): void {
    if (!this.cache) {
      // Synchronously initialize with basic data if cache is not ready
      this.cache = {
        careers: this.extractArray(careersData as unknown as JsonDataStructure<Career>, 'careers'),
        homeworlds: this.extractArray(
          homeworldsData as unknown as JsonDataStructure<Homeworld>,
          'homeworlds'
        ),
        birthrights: this.extractArray(
          birthrightsData as unknown as JsonDataStructure<Birthright>,
          'birthrights'
        ),
        lures: this.extractArray(luresData as unknown as JsonDataStructure<LureOfTheVoid>, 'lures'),
        trials: this.extractArray(trialsData as unknown as JsonDataStructure<Trials>, 'trials'),
        motivations: this.extractArray(
          motivationsData as unknown as JsonDataStructure<Motivation>,
          'motivations'
        ),
        skills: this.extractArray(skillsData as unknown as JsonDataStructure<Skill>, 'skills'),
        talents: this.extractArray(talentsData as unknown as JsonDataStructure<Talent>, 'talents'),
        traits: this.extractArray(traitsData as unknown as JsonDataStructure<Trait>, 'traits'),
        weapons: [], // Weapons are derived from items.json
        armor: [], // Armor is derived from items.json
        equipment: [], // Equipment is derived from items.json
        shopItems: this.extractArray(
          shopItemsData as unknown as JsonDataStructure<ShopItem>,
          'items'
        ),
        psychicPowers: this.extractArray(
          psychicPowersData as unknown as JsonDataStructure<PsychicPowerEntry>,
          'powers'
        ),
        lastValidated: new Date(),
        validationResults: {}
      };
    }

    // Check if cache needs refresh
    const now = new Date();
    if (now.getTime() - this.cache.lastValidated.getTime() > this.CACHE_DURATION) {
      this.initializeData(); // Refresh cache asynchronously
    }
  }

  /**
   * Get validation results for all data files
   */
  public getValidationResults(): Record<string, ValidationResult> {
    this.ensureDataLoaded();
    return this.cache?.validationResults || {};
  }

  /**
   * Get data integrity validation results
   */
  public getIntegrityResults(): DataIntegrityResult | undefined {
    this.ensureDataLoaded();
    return this.cache?.integrityResult;
  }

  /**
   * Force refresh of all cached data
   */
  public async refreshData(): Promise<void> {
    this.cache = null;
    await this.initializeData();
  }

  /**
   * Get all careers, optionally filtered by enabled rulebooks
   */
  public getCareers(enabledRulebooks?: string[]): Career[] {
    this.ensureDataLoaded();
    const careers = this.cache?.careers || [];
    if (!enabledRulebooks) {
      return careers;
    }
    return careers.filter((career) => isRulebookEnabled(career.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific career by ID
   */
  public getCareer(id: string): Career | undefined {
    this.ensureDataLoaded();
    return this.cache?.careers.find((career) => career.id === id);
  }

  /**
   * Get all homeworlds, optionally filtered by enabled rulebooks
   */
  public getHomeworlds(enabledRulebooks?: string[]): Homeworld[] {
    this.ensureDataLoaded();
    const homeworlds = this.cache?.homeworlds || [];
    if (!enabledRulebooks) {
      return homeworlds;
    }
    return homeworlds.filter((homeworld) =>
      isRulebookEnabled(homeworld.rulebook, enabledRulebooks)
    );
  }

  /**
   * Get a specific homeworld by ID
   */
  public getHomeworld(id: string): Homeworld | undefined {
    this.ensureDataLoaded();
    return this.cache?.homeworlds.find((homeworld) => homeworld.id === id);
  }

  /**
   * Get all birthrights, optionally filtered by enabled rulebooks
   */
  public getBirthrights(enabledRulebooks?: string[]): Birthright[] {
    this.ensureDataLoaded();
    const birthrights = this.cache?.birthrights || [];
    if (!enabledRulebooks) {
      return birthrights;
    }
    return birthrights.filter((birthright) =>
      isRulebookEnabled(birthright.rulebook, enabledRulebooks)
    );
  }

  /**
   * Get a specific birthright by ID
   */
  public getBirthright(id: string): Birthright | undefined {
    this.ensureDataLoaded();
    return this.cache?.birthrights.find((birthright) => birthright.id === id);
  }

  /**
   * Get all lures of the void, optionally filtered by enabled rulebooks
   */
  public getLures(enabledRulebooks?: string[]): LureOfTheVoid[] {
    this.ensureDataLoaded();
    const lures = this.cache?.lures || [];
    if (!enabledRulebooks) {
      return lures;
    }
    return lures.filter((lure) => isRulebookEnabled(lure.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific lure by ID
   */
  public getLure(id: string): LureOfTheVoid | undefined {
    this.ensureDataLoaded();
    return this.cache?.lures.find((lure) => lure.id === id);
  }

  /**
   * Get all trials, optionally filtered by enabled rulebooks
   */
  public getTrials(enabledRulebooks?: string[]): Trials[] {
    this.ensureDataLoaded();
    const trials = this.cache?.trials || [];
    if (!enabledRulebooks) {
      return trials;
    }
    return trials.filter((trial) => isRulebookEnabled(trial.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific trial by ID
   */
  public getTrial(id: string): Trials | undefined {
    this.ensureDataLoaded();
    return this.cache?.trials.find((trial) => trial.id === id);
  }

  /**
   * Get all motivations, optionally filtered by enabled rulebooks
   */
  public getMotivations(enabledRulebooks?: string[]): Motivation[] {
    this.ensureDataLoaded();
    const motivations = this.cache?.motivations || [];
    if (!enabledRulebooks) {
      return motivations;
    }
    return motivations.filter((motivation) =>
      isRulebookEnabled(motivation.rulebook, enabledRulebooks)
    );
  }

  /**
   * Get a specific motivation by ID
   */
  public getMotivation(id: string): Motivation | undefined {
    this.ensureDataLoaded();
    return this.cache?.motivations.find((motivation) => motivation.id === id);
  }

  /**
   * Get all skills, optionally filtered by enabled rulebooks
   */
  public getSkills(enabledRulebooks?: string[]): Skill[] {
    this.ensureDataLoaded();
    const skills = this.cache?.skills || [];
    if (!enabledRulebooks) {
      return skills;
    }
    return skills.filter((skill) => isRulebookEnabled(skill.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific skill by string ID
   */
  public getSkillByStringId(id: string): Skill | undefined {
    this.ensureDataLoaded();
    return this.cache?.skills.find((skill) => skill.id === id);
  }

  /**
   * Get all talents, optionally filtered by enabled rulebooks
   */
  public getTalents(enabledRulebooks?: string[]): Talent[] {
    this.ensureDataLoaded();
    const talents = this.cache?.talents || [];
    if (!enabledRulebooks) {
      return talents;
    }
    return talents.filter((talent) => isRulebookEnabled(talent.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific talent by string ID
   */
  public getTalentByStringId(id: string): Talent | undefined {
    this.ensureDataLoaded();
    return this.cache?.talents.find((talent) => talent.id === id);
  }

  /**
   * Get all traits, optionally filtered by enabled rulebooks
   */
  public getTraits(enabledRulebooks?: string[]): Trait[] {
    this.ensureDataLoaded();
    const traits = this.cache?.traits || [];
    if (!enabledRulebooks) {
      return traits;
    }
    return traits.filter((trait) => isRulebookEnabled(trait.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific trait by ID
   */
  public getTrait(id: string): Trait | undefined {
    this.ensureDataLoaded();
    return this.cache?.traits.find((trait) => trait.id === id);
  }

  /**
   * Get all weapons, optionally filtered by enabled rulebooks
   */
  public getWeapons(enabledRulebooks?: string[]): Weapon[] {
    this.ensureDataLoaded();
    const weapons = this.cache?.weapons || [];
    if (!enabledRulebooks) {
      return weapons;
    }
    return weapons.filter((weapon) => isRulebookEnabled(weapon.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific weapon by ID
   */
  public getWeapon(id: string): Weapon | undefined {
    this.ensureDataLoaded();
    return this.cache?.weapons.find((weapon) => weapon.id === id);
  }

  /**
   * Get all armor, optionally filtered by enabled rulebooks
   */
  public getArmor(enabledRulebooks?: string[]): Armor[] {
    this.ensureDataLoaded();
    const armor = this.cache?.armor || [];
    if (!enabledRulebooks) {
      return armor;
    }
    return armor.filter((armorPiece) => isRulebookEnabled(armorPiece.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific armor piece by ID
   */
  public getArmorPiece(id: string): Armor | undefined {
    this.ensureDataLoaded();
    return this.cache?.armor.find((armorPiece) => armorPiece.id === id);
  }

  /**
   * Get all equipment, optionally filtered by enabled rulebooks
   */
  public getEquipment(enabledRulebooks?: string[]): Equipment[] {
    this.ensureDataLoaded();
    const equipment = this.cache?.equipment || [];
    if (!enabledRulebooks) {
      return equipment;
    }
    return equipment.filter((item) => isRulebookEnabled(item.rulebook, enabledRulebooks));
  }

  /**
   * Get a specific equipment item by string ID
   */
  public getEquipmentItem(id: string): Equipment | undefined {
    this.ensureDataLoaded();
    return this.cache?.equipment.find((item) => item.id === id);
  }

  /**
   * Get a shop item by string ID (e.g., "laspistol", "hand-cannon")
   */
  public getShopItemByStringId(id: string): ShopItem | undefined {
    this.ensureDataLoaded();
    return this.cache?.shopItems.find((item) => item.id === id);
  }

  /**
   * Get all shop items from items.json
   */
  public getShopItems(): ShopItem[] {
    this.ensureDataLoaded();
    return this.cache?.shopItems || [];
  }

  /**
   * Get a weapon by string ID with optional quality and modifications
   */
  public getWeaponByStringId(
    id: string,
    options?: { quality?: string; upgrade?: string }
  ): Weapon | undefined {
    const item = this.getShopItemByStringId(id);
    if (!item || item.type !== 'weapon') return undefined;

    // Get weapon stats from items.json weaponStats field
    const itemWithStats = item as ShopItem & {
      weaponStats?: {
        damage?: string;
        penetration?: number;
        range?: string;
        rateOfFire?: {
          single?: string | number;
          semiAuto?: string | number;
          fullAuto?: string | number;
        };
        clip?: number;
        reload?: string;
        special?: string | string[];
      };
    };
    const wd = itemWithStats.weaponStats || {};

    // Weapon qualities arrive as either a comma-separated string or an array
    const special: string[] = parseCommaSeparated(wd.special);

    // Parse range - can be string like "30m" or number
    const range =
      typeof wd.range === 'string' ? parseInt(wd.range.replace(/\D/g, '') || '0') : wd.range || 0;

    // Apply quality as craftsmanship (capitalize first letter)
    const craftsmanship = options?.quality
      ? ((options.quality.charAt(0).toUpperCase() + options.quality.slice(1)) as
          | 'Poor'
          | 'Common'
          | 'Good'
          | 'Best')
      : undefined;

    // Build modifications array
    const modifications: string[] = [];
    if (options?.upgrade) {
      modifications.push(options.upgrade);
    }

    return {
      id: item.id,
      sourceId: item.id,
      type: 'weapon' as const,
      weight: item.weight,
      availability: item.availability,
      cost: item.cost ?? undefined,
      description: item.description,
      rulebook: item.rulebook,
      class: (wd as { class?: string }).class || item.weaponClass || 'basic',
      range: range,
      rateOfFire: wd.rateOfFire
        ? {
            single:
              typeof wd.rateOfFire.single === 'string'
                ? wd.rateOfFire.single === 'S'
                  ? 1
                  : parseInt(wd.rateOfFire.single) || 0
                : wd.rateOfFire.single || 0,
            semiAuto:
              typeof wd.rateOfFire.semiAuto === 'string'
                ? parseInt(wd.rateOfFire.semiAuto) || 0
                : wd.rateOfFire.semiAuto || 0,
            fullAuto:
              typeof wd.rateOfFire.fullAuto === 'string'
                ? parseInt(wd.rateOfFire.fullAuto) || 0
                : wd.rateOfFire.fullAuto || 0
          }
        : { single: 1, semiAuto: 0, fullAuto: 0 },
      damage: wd.damage || '1d10',
      penetration: wd.penetration || 0,
      clip: wd.clip || 0,
      reload: wd.reload || '-',
      special: special,
      craftsmanship,
      modifications: modifications.length > 0 ? modifications : undefined
    } as Weapon;
  }

  /**
   * Get armor by string ID with optional quality
   */
  public getArmorByStringId(id: string, options?: { quality?: string }): Armor | undefined {
    const item = this.getShopItemByStringId(id);
    if (!item || item.type !== 'armor') return undefined;

    const ad = item.armorData || {};

    // Apply quality as craftsmanship (capitalize first letter)
    const craftsmanship = options?.quality
      ? ((options.quality.charAt(0).toUpperCase() + options.quality.slice(1)) as
          | 'Poor'
          | 'Common'
          | 'Good'
          | 'Best')
      : undefined;

    return {
      id: item.id,
      sourceId: item.id,
      type: 'armor' as const,
      weight: item.weight,
      availability: item.availability,
      cost: item.cost ?? undefined,
      description: item.description,
      rulebook: item.rulebook,
      locations: ad.locations || ['body'],
      armorPoints: ad.armorPoints || 0,
      special: ad.special || [],
      craftsmanship
    } as Armor;
  }

  /**
   * Get equipment (gear/clothing) by string ID with optional quality
   */
  public getEquipmentByStringId(id: string, options?: { quality?: string }): Equipment | undefined {
    const item = this.getShopItemByStringId(id);
    if (!item || item.type === 'weapon' || item.type === 'armor') return undefined;

    // Apply quality as craftsmanship (capitalize first letter)
    const craftsmanship = options?.quality
      ? ((options.quality.charAt(0).toUpperCase() + options.quality.slice(1)) as
          | 'Poor'
          | 'Common'
          | 'Good'
          | 'Best')
      : undefined;

    return {
      id: item.id,
      sourceId: item.id,
      type: item.type as Equipment['type'],
      weight: item.weight,
      availability: item.availability,
      cost: item.cost ?? undefined,
      description: item.description,
      rulebook: item.rulebook,
      craftsmanship
    } as Equipment;
  }

  /**
   * Get display name for an equipment item by source ID
   * Returns the source ID as fallback if not found
   *
   * Requirements: 2.1, 2.3
   */
  public getItemDisplayName(sourceId: string): string {
    const item = this.getShopItemByStringId(sourceId);
    return item?.name ?? sourceId;
  }

  /**
   * Get display name with craftsmanship prefix
   * "Common" craftsmanship does not add a prefix
   *
   * Requirements: 4.3
   */
  public getItemDisplayNameWithQuality(
    sourceId: string,
    craftsmanship?: 'Poor' | 'Common' | 'Good' | 'Best'
  ): string {
    const baseName = this.getItemDisplayName(sourceId);
    if (craftsmanship && craftsmanship !== 'Common') {
      return `${craftsmanship} ${baseName}`;
    }
    return baseName;
  }

  /**
   * Get all available rulebooks from the loaded data
   */
  public getPsychicPowers(): PsychicPowerEntry[] {
    this.ensureDataLoaded();
    return this.cache?.psychicPowers || [];
  }

  public getPsychicPowerById(id: string): PsychicPowerEntry | undefined {
    this.ensureDataLoaded();
    return this.cache?.psychicPowers.find((p) => p.id === id);
  }

  public getPsychicPowersByDiscipline(discipline: string): PsychicPowerEntry[] {
    this.ensureDataLoaded();
    return (this.cache?.psychicPowers || []).filter(
      (p) => p.school.toLowerCase() === discipline.toLowerCase()
    );
  }

  public getAvailableRulebooks(): string[] {
    this.ensureDataLoaded();
    const rulebooks = new Set<string>();

    const addRulebook = (rb: string | string[]) => {
      if (Array.isArray(rb)) rb.forEach((r) => rulebooks.add(r));
      else rulebooks.add(rb);
    };

    if (this.cache) {
      this.cache.careers.forEach((career) => addRulebook(career.rulebook));
      this.cache.homeworlds.forEach((homeworld) => addRulebook(homeworld.rulebook));
      this.cache.birthrights.forEach((birthright) => addRulebook(birthright.rulebook));
      this.cache.lures.forEach((lure) => addRulebook(lure.rulebook));
      this.cache.trials.forEach((trial) => addRulebook(trial.rulebook));
      this.cache.motivations.forEach((motivation) => addRulebook(motivation.rulebook));
      this.cache.skills.forEach((skill) => addRulebook(skill.rulebook));
      this.cache.talents.forEach((talent) => addRulebook(talent.rulebook));
      this.cache.traits.forEach((trait) => addRulebook(trait.rulebook));
      this.cache.weapons.forEach((weapon) => addRulebook(weapon.rulebook));
      this.cache.armor.forEach((armor) => addRulebook(armor.rulebook));
      this.cache.equipment.forEach((equipment) => addRulebook(equipment.rulebook));
    }

    return Array.from(rulebooks).sort();
  }

  /**
   * Validate that all referenced IDs exist in the data
   */
  public validateDataIntegrity(): { isValid: boolean; errors: string[] } {
    this.ensureDataLoaded();
    const integrityResult = this.cache?.integrityResult;

    if (integrityResult) {
      return {
        isValid: integrityResult.isValid,
        errors: integrityResult.errors
      };
    }

    // Fallback validation if integrity result is not available
    const errors: string[] = [];
    if (this.cache) {
      const checkDuplicates = <T extends { id: string; name?: string; sourceId?: string }>(
        items: T[],
        category: string
      ) => {
        const ids = new Set<string>();
        items.forEach((item) => {
          if (ids.has(item.id)) {
            const displayName = item.name || item.sourceId || item.id;
            errors.push(`Duplicate ${category} ID: ${item.id} (${displayName})`);
          }
          ids.add(item.id);
        });
      };

      checkDuplicates(this.cache.careers, 'career');
      checkDuplicates(this.cache.homeworlds, 'homeworld');
      checkDuplicates(this.cache.birthrights, 'birthright');
      checkDuplicates(this.cache.lures, 'lure');
      checkDuplicates(this.cache.trials, 'trial');
      checkDuplicates(this.cache.motivations, 'motivation');
      checkDuplicates(this.cache.skills, 'skill');
      checkDuplicates(this.cache.talents, 'talent');
      checkDuplicates(this.cache.traits, 'trait');
      checkDuplicates(this.cache.weapons, 'weapon');
      checkDuplicates(this.cache.armor, 'armor');
      checkDuplicates(this.cache.equipment, 'equipment');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const gameDataService = GameDataService.getInstance();
